import type {
  ActiveFilter,
  CategoryFilters,
} from '@/features/games/internal/types/category-filters';
import { getLanguageName } from '@/shared/utils/get-language-name';

export type SearchParams = Record<string, string | string[] | undefined>;

/**
 * Extracts active filters from search params for category filters
 */
export function getActiveFiltersFromParams(
  searchParams: SearchParams,
  categoryFilters: CategoryFilters,
  t: (key: string) => string,
): ActiveFilter[] {
  const activeFilters: ActiveFilter[] = [];
  const knownAttributes = ['wild benefits', 'game series', 'tag', 'language'];

  Object.keys(searchParams).forEach((key) => {
    const lowerKey = key.toLowerCase();
    if (knownAttributes.includes(lowerKey)) {
      const values = getParamValue(searchParams, key);
      if (values) {
        const isLanguageFilter = lowerKey === 'language';
        values.split(',').forEach((value) => {
          const trimmedValue = value.trim();
          const label = isLanguageFilter ? getLanguageName(trimmedValue) : trimmedValue;
          activeFilters.push({ type: key, value: trimmedValue, label });
        });
      }
    }
  });

  const brands = getParamValue(searchParams, 'brands');
  if (brands) {
    brands.split(',').forEach((value) => {
      const brandId = value.trim();
      const brandName =
        categoryFilters?.brands?.options.find((opt) => opt.value === brandId)?.label || brandId;
      activeFilters.push({ type: 'brands', value: brandId, label: brandName });
    });
  }

  const categories = getParamValue(searchParams, 'categories');
  if (categories) {
    categories.split(',').forEach((value) => {
      const categoryId = value.trim();
      const categoryName =
        categoryFilters?.categories?.options.find((opt) => opt.value === categoryId)?.label ||
        categoryId;
      activeFilters.push({ type: 'categories', value: categoryId, label: categoryName });
    });
  }

  const ratingMin = getParamValue(searchParams, 'ratingMin');
  if (ratingMin) {
    const ratingValue = ratingMin.trim();
    const stars = Number(ratingValue);
    const ratingLabel = `${t('rated')} ${stars} ${stars === 1 ? t('star') : t('stars')} ${t('orMore')}`;
    activeFilters.push({ type: 'ratingMin', value: ratingValue, label: ratingLabel });
  }

  Object.keys(searchParams).forEach((key) => {
    if (key.startsWith('attr.')) {
      const attrName = key.slice(5);
      const values = getParamValue(searchParams, key);
      if (values) {
        values.split(',').forEach((value) => {
          activeFilters.push({
            type: key,
            value: value.trim(),
            label: `${attrName}: ${value.trim()}`,
          });
        });
      }
    }
  });

  return activeFilters;
}

/**
 * Toggles a filter value in the URL params
 */
export function toggleFilterInParams(
  searchParams: SearchParams,
  filterType: string,
  filterValue: string,
): URLSearchParams {
  const params = new URLSearchParams();

  Object.entries(searchParams).forEach(([key, value]) => {
    if (key === 'page') return;
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, v));
    } else if (value != null) {
      params.set(key, String(value));
    }
  });

  const currentValue = params.get(filterType) || '';
  const currentValues = currentValue ? currentValue.split(',').map((v) => v.trim()) : [];

  if (currentValues.includes(filterValue)) {
    const newValues = currentValues.filter((v) => v !== filterValue);
    if (newValues.length > 0) {
      params.set(filterType, newValues.join(','));
    } else {
      params.delete(filterType);
    }
  } else {
    currentValues.push(filterValue);
    params.set(filterType, currentValues.join(','));
  }

  return params;
}

/**
 * Removes a specific filter from URL params
 */
export function removeFilterFromParams(
  searchParams: SearchParams,
  filterType: string,
  filterValue: string,
): URLSearchParams {
  const params = new URLSearchParams();

  Object.entries(searchParams).forEach(([key, value]) => {
    if (key === 'page') return;
    if (key === filterType) {
      const currentValue = Array.isArray(value) ? value[0] : value;
      if (currentValue) {
        const values = currentValue.split(',').map((v) => v.trim());
        const newValues = values.filter((v) => v !== filterValue);
        if (newValues.length > 0) {
          params.set(key, newValues.join(','));
        }
      }
    } else if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, v));
    } else if (value != null) {
      params.set(key, String(value));
    }
  });

  return params;
}

/**
 * Clears all filters from URL params
 */
export function clearAllFilters(searchParams: SearchParams): URLSearchParams {
  const params = new URLSearchParams();

  const sort = getParamValue(searchParams, 'sort');
  if (sort) {
    params.set('sort', sort);
  }

  const q = getParamValue(searchParams, 'search_query');
  if (q) {
    params.set('search_query', q);
  }

  return params;
}

/**
 * Builds a search URL with additional filter parameters
 */
export function buildSearchUrlWithFilter(
  searchParams: SearchParams,
  filterType: string,
  filterValue: string,
): string {
  const params = new URLSearchParams();
  Object.entries(searchParams).forEach(([key, value]) => {
    if (key === 'page') return;
    const normalizedValue = Array.isArray(value) ? value[0] : (value ?? '');
    if (normalizedValue) {
      params.set(key, normalizedValue);
    }
  });
  const existing = params.get(filterType);
  params.set(filterType, existing ? `${existing},${filterValue}` : filterValue);
  return `/search?${params.toString()}`;
}

/**
 * Helper to get a single string value from search params
 */
function getParamValue(searchParams: SearchParams, key: string): string | undefined {
  const value = searchParams[key];
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
}
